import { Badge } from "../../../app/components/ui/badge";
import { Button } from "../../../app/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../app/components/ui/collapsible";
import { formatInr, formatInrTrimmed } from "../../../lib/currency";
import { ChevronDown, Minus, Plus } from "lucide-react";
import type { PosCartItem, PosGstLineBreakdown } from "../types";
import { PosCartItemGstPanel } from "./PosCartItemGstPanel";

export function PosCartItemCard({
  item,
  gst,
  expanded,
  onExpandedChange,
  onQtyDelta,
  onChangeOverride,
}: {
  item: PosCartItem;
  gst: PosGstLineBreakdown;
  expanded: boolean;
  onExpandedChange: (open: boolean) => void;
  onQtyDelta: (nextQty: number) => void;
  onChangeOverride: (patch: Partial<Pick<PosCartItem, "gst_override_type" | "gst_override_value" | "gst_override_reason">>) => void;
}) {
  const lineQty = item.quantity;
  const unit = item.unit_price;
  const overridden = (item.gst_override_type ?? "none") !== "none";

  return (
    <Collapsible open={expanded} onOpenChange={onExpandedChange}>
      <div className="border rounded-lg p-3">
        <CollapsibleTrigger asChild>
          <button type="button" className="w-full text-left flex justify-between items-start gap-2 group">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-sm">{item.name}</p>
                {overridden ? (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 border-amber-500/60 text-amber-800 dark:text-amber-200">
                    GST overridden
                  </Badge>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {lineQty} × {formatInrTrimmed(unit)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                GST: {formatInr(gst.gst)}
                {gst.effectiveRatePercent != null ? ` (${gst.effectiveRatePercent}%)` : ""}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className="font-semibold text-primary text-sm">{formatInr(gst.total)}</span>
              <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
                Details
              </span>
            </div>
          </button>
        </CollapsibleTrigger>

        <div className="flex items-center justify-between gap-2 flex-wrap mt-2">
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            <Button
              size="icon"
              variant="outline"
              className="h-8 w-8 min-h-9 min-w-9 shrink-0 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onQtyDelta(item.quantity - 1);
              }}
            >
              <Minus className="w-3 h-3" />
            </Button>
            <span className="w-8 text-center font-medium tabular-nums">{item.quantity}</span>
            <Button
              size="icon"
              variant="outline"
              className="h-8 w-8 min-h-9 min-w-9 shrink-0 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onQtyDelta(item.quantity + 1);
              }}
              disabled={item.quantity >= item.available_qty}
            >
              <Plus className="w-3 h-3" />
            </Button>
            <div className="flex items-center gap-1 pl-1">
              {([1, 5, 10] as const).map((n) => (
                <Button
                  key={n}
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="h-8 min-h-9 px-2 text-xs font-medium [@media(pointer:coarse)]:min-h-11"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQtyDelta(Math.min(item.available_qty, item.quantity + n));
                  }}
                  disabled={item.quantity >= item.available_qty}
                  aria-label={`Add ${n} to quantity`}
                >
                  +{n}
                </Button>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Total: <span className="font-medium text-foreground">{formatInr(gst.total)}</span>
          </p>
        </div>

        <CollapsibleContent>
          <PosCartItemGstPanel item={item} gst={gst} onChangeOverride={onChangeOverride} />
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
